import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { SelectFieldComponent } from '../common/SelectFieldComponent';
import { CatalogService } from '../../services/catalogService';
import { type Encargado } from '../../types/catalog';
import { EditEncargado } from '../../features/catalogo/encargados/components';
import { type EncargadoRequest } from '../../features/catalogo/encargados/types/encargado';
import { EncargadoService } from '../../features/catalogo/encargados/services/encargadoService';

interface EncargadoSelectProps {
  value: number | string;
  onChange: (value: number) => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  allowCreate?: boolean;
}

export const EncargadoSelect: React.FC<EncargadoSelectProps> = ({
  value,
  onChange,
  label = 'Encargado',
  error,
  required = false,
  disabled = false,
  fullWidth = true,
  allowCreate = true,
}) => {
  const [encargados, setEncargados] = useState<Encargado[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    loadEncargados();
  }, []);

  const loadEncargados = async () => {
    try {
      setLoading(true);
      const data = await CatalogService.getEncargados();
      setEncargados(data);
    } catch (error) {
      console.error('Error loading encargados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNewEncargado = async (request: EncargadoRequest) => {
    const saved = await EncargadoService.saveEncargado(request);
    await loadEncargados();
    onChange(saved.id);
    setCreateDialogOpen(false);
  };

  const options = encargados.map(encargado => ({
    value: encargado.id,
    label: `${encargado.nombres} ${encargado.apellidos}`,
  }));

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <SelectFieldComponent
            label={label}
            value={value}
            onChange={(val) => onChange(Number(val))}
            options={options}
            error={error}
            required={required}
            disabled={disabled || loading}
            fullWidth={fullWidth}
            placeholder={loading ? 'Cargando...' : 'Seleccione un encargado'}
          />
        </Box>
        {allowCreate && !disabled && (
          <Tooltip title="Nuevo Encargado">
            <IconButton
              size="small"
              color="primary"
              onClick={() => setCreateDialogOpen(true)}
              sx={{ mt: 0.5 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <EditEncargado
        open={createDialogOpen}
        encargado={null}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleSaveNewEncargado}
        title="Nuevo Encargado"
      />
    </>
  );
};
